/**
 * FieldRenderer
 * Renders a form field based on its schema type
 */

import * as React from 'react';
import { cn } from '../lib/utils';
import type { KitTranslateFn } from '../lib/kitTranslate';
import { Label } from '../components/ui/label';
import type { FieldSchema } from '../framework/ElementSchema';

export interface FieldRendererProps {
    /** Field schema */
    field: FieldSchema;
    /** Current value */
    value: unknown;
    /** Called when value changes */
    onChange: (value: unknown) => void;
    /** Error message */
    error?: string;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Additional class name */
    className?: string;
    /** Optional i18n for kit fields (e.g. rich text toolbar) */
    translate?: KitTranslateFn;
}

/**
 * FieldRenderer - Renders appropriate field component based on schema type
 */
export const FieldRenderer: React.FC<FieldRendererProps> = ({
    field,
    value,
    onChange,
    error,
    disabled,
    className,
    translate,
}) => {
    const isRequired = field.required || field.validation?.required;

    return (
        <div className={cn('field-renderer space-y-2', className)}>
            {/* Label */}
            <div className="flex items-center justify-between">
                <Label className={cn('text-sm font-medium', error && 'text-destructive')}>
                    {field.label}
                    {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
            </div>

            {/* Description */}
            {field.description && (
                <p className="text-xs text-muted-foreground">{field.description}</p>
            )}

            {/* Field Input */}
            <FieldInput
                field={field}
                value={value}
                onChange={onChange}
                disabled={disabled || field.disabled}
                error={error}
                translate={translate}
            />

            {/* Error Message */}
            {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
    );
};

/**
 * FieldInput - Renders the actual input component
 */
interface FieldInputProps {
    field: FieldSchema;
    value: unknown;
    onChange: (value: unknown) => void;
    disabled?: boolean;
    error?: string;
    translate?: KitTranslateFn;
}

const FieldInput: React.FC<FieldInputProps> = ({
    field,
    value,
    onChange,
    disabled,
    error,
    translate,
}) => {
    switch (field.type) {
        case 'text':
            return (
                <TextFieldInput
                    field={field}
                    value={value as string}
                    onChange={onChange}
                    disabled={disabled}
                    error={error}
                />
            );

        case 'number':
            return (
                <NumberFieldInput
                    field={field}
                    value={value as number}
                    onChange={onChange}
                    disabled={disabled}
                    error={error}
                />
            );

        case 'boolean':
            return (
                <BooleanFieldInput
                    value={value as boolean}
                    onChange={onChange}
                    disabled={disabled}
                />
            );

        case 'select':
            return (
                <SelectFieldInput
                    field={field}
                    value={value as string}
                    onChange={onChange}
                    disabled={disabled}
                    error={error}
                />
            );

        case 'multiselect':
            return (
                <MultiselectFieldInput
                    field={field}
                    value={value as string[]}
                    onChange={onChange}
                    disabled={disabled}
                />
            );

        case 'rich-text':
            return (
                <RichTextFieldInput
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    error={error}
                    translate={translate}
                />
            );

        case 'media':
            return (
                <MediaFieldInput
                    field={field}
                    value={value as string}
                    onChange={onChange}
                    disabled={disabled}
                    error={error}
                />
            );

        case 'url':
            return (
                <UrlFieldInput
                    field={field}
                    value={value as string}
                    onChange={onChange}
                    disabled={disabled}
                    error={error}
                />
            );

        case 'color':
            return (
                <ColorFieldInput value={value as string} onChange={onChange} disabled={disabled} />
            );

        case 'list':
            return (
                <ListFieldInput
                    field={field}
                    value={value as unknown[]}
                    onChange={onChange}
                    disabled={disabled}
                    translate={translate}
                />
            );

        case 'group':
            return (
                <GroupFieldInput
                    field={field}
                    value={value as Record<string, unknown>}
                    onChange={onChange}
                    disabled={disabled}
                    translate={translate}
                />
            );

        default:
            return (
                <div className="text-sm text-muted-foreground italic">
                    Unknown field type: {(field as FieldSchema).type}
                </div>
            );
    }
};

// ============================================
// Field Input Components
// ============================================

import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { RichTextEditor } from '../components/content/rich-text';
import { createEmptyDocument, isTiptapDocument, markdownToTiptap } from '../lib/tiptap';
import type { TiptapJSON } from '../lib/tiptap';
import { getMarkdownString } from '../lib/rich-text-detect';
import type {
    TextFieldSchema,
    NumberFieldSchema,
    SelectFieldSchema,
    MediaFieldSchema,
    UrlFieldSchema,
    ListFieldSchema,
    GroupFieldSchema,
} from '../framework/ElementSchema';

// Text Field
const TextFieldInput: React.FC<{
    field: TextFieldSchema;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    error?: string;
}> = ({ field, value, onChange, disabled, error }) => {
    const Component = field.multiline ? Textarea : Input;

    return (
        <Component
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            maxLength={field.maxLength}
            className={cn(error && 'border-destructive')}
        />
    );
};

// Number Field
const NumberFieldInput: React.FC<{
    field: NumberFieldSchema;
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
    error?: string;
}> = ({ field, value, onChange, disabled, error }) => {
    return (
        <Input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            placeholder={field.placeholder}
            disabled={disabled}
            min={field.min}
            max={field.max}
            step={field.step}
            className={cn(error && 'border-destructive')}
        />
    );
};

// Boolean Field
const BooleanFieldInput: React.FC<{
    value: boolean;
    onChange: (value: boolean) => void;
    disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
    return <Switch checked={value || false} onCheckedChange={onChange} disabled={disabled} />;
};

// Select Field
const SelectFieldInput: React.FC<{
    field: SelectFieldSchema;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    error?: string;
}> = ({ field, value, onChange, disabled, error }) => {
    return (
        <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger className={cn(error && 'border-destructive')}>
                <SelectValue placeholder={field.placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
                {field.options.map((option) => (
                    <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

// Multiselect Field
const MultiselectFieldInput: React.FC<{
    field: SelectFieldSchema;
    value: string[];
    onChange: (value: string[]) => void;
    disabled?: boolean;
}> = ({ field, value = [], onChange, disabled }) => {
    const toggleOption = (optionValue: string) => {
        if (value.includes(optionValue)) {
            onChange(value.filter((v) => v !== optionValue));
        } else {
            onChange([...value, optionValue]);
        }
    };

    return (
        <div className="space-y-2">
            {field.options.map((option) => (
                <label
                    key={option.value}
                    className={cn(
                        'flex items-center gap-2 cursor-pointer',
                        disabled && 'opacity-50 cursor-not-allowed'
                    )}
                >
                    <Checkbox
                        checked={value.includes(option.value)}
                        onCheckedChange={() => toggleOption(option.value)}
                        disabled={disabled || option.disabled}
                    />
                    <span className="text-sm">{option.label}</span>
                </label>
            ))}
        </div>
    );
};

/** Normalize value to Tiptap JSON format */
function toTiptapValue(value: unknown): TiptapJSON {
    if (isTiptapDocument(value)) {
        return value;
    }
    const markdownStr = getMarkdownString(value);
    if (markdownStr != null && markdownStr.trim() !== '') {
        return markdownToTiptap(markdownStr);
    }
    return createEmptyDocument();
}

// Rich Text Field - uses Editor.js for block-based rich text editing
const RichTextFieldInput: React.FC<{
    value: unknown;
    onChange: (value: unknown) => void;
    disabled?: boolean;
    error?: string;
    translate?: KitTranslateFn;
}> = ({ value, onChange, disabled, error, translate }) => {
    const editorValue = toTiptapValue(value);

    return (
        <RichTextEditor
            value={editorValue}
            onChange={(data) => onChange(data)}
            readOnly={disabled}
            placeholder="Start typing or press Tab for more options..."
            minHeight={200}
            error={!!error}
            translate={translate}
        />
    );
};

// Media Field
const MediaFieldInput: React.FC<{
    field: MediaFieldSchema;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    error?: string;
}> = ({ field, value, onChange, disabled, error }) => {
    return (
        <div className="space-y-2">
            <Input
                type="url"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={field.placeholder || 'Enter URL or upload...'}
                disabled={disabled}
                className={cn(error && 'border-destructive')}
            />
            {value && field.mediaType === 'image' && (
                <div className="mt-2 rounded-lg overflow-hidden border bg-muted/50">
                    <img src={value} alt="Preview" className="max-h-32 object-contain mx-auto" />
                </div>
            )}
        </div>
    );
};

// URL Field
const UrlFieldInput: React.FC<{
    field: UrlFieldSchema;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    error?: string;
}> = ({ field, value, onChange, disabled, error }) => {
    return (
        <Input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || 'https://...'}
            disabled={disabled}
            className={cn(error && 'border-destructive')}
        />
    );
};

// Color Field
const ColorFieldInput: React.FC<{
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
    return (
        <div className="flex items-center gap-2">
            <input
                type="color"
                value={value || '#000000'}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="w-10 h-10 rounded border cursor-pointer"
            />
            <Input
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#000000"
                disabled={disabled}
                className="flex-1"
            />
        </div>
    );
};

// List Field
const ListFieldInput: React.FC<{
    field: ListFieldSchema;
    value: unknown[];
    onChange: (value: unknown[]) => void;
    disabled?: boolean;
    translate?: KitTranslateFn;
}> = ({ field, value = [], onChange, disabled, translate }) => {
    const addItem = () => {
        const defaultValue = field.itemSchema.defaultValue ?? '';
        onChange([...value, defaultValue]);
    };

    const removeItem = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const updateItem = (index: number, itemValue: unknown) => {
        const newValue = [...value];
        newValue[index] = itemValue;
        onChange(newValue);
    };

    const canAdd = !field.maxItems || value.length < field.maxItems;
    const canRemove = !field.minItems || value.length > field.minItems;

    return (
        <div className="space-y-2">
            {value.map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                    {field.sortable && (
                        <div className="mt-2 cursor-move text-muted-foreground">
                            <GripVertical className="h-4 w-4" />
                        </div>
                    )}
                    <div className="flex-1">
                        <FieldRenderer
                            field={{ ...field.itemSchema, label: `Item ${index + 1}` }}
                            value={item}
                            onChange={(v) => updateItem(index, v)}
                            disabled={disabled}
                            translate={translate}
                        />
                    </div>
                    {canRemove && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(index)}
                            disabled={disabled}
                            className="mt-1"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            ))}
            {canAdd && (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addItem}
                    disabled={disabled}
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                </Button>
            )}
        </div>
    );
};

// Group Field
const GroupFieldInput: React.FC<{
    field: GroupFieldSchema;
    value: Record<string, unknown>;
    onChange: (value: Record<string, unknown>) => void;
    disabled?: boolean;
    translate?: KitTranslateFn;
}> = ({ field, value = {}, onChange, disabled, translate }) => {
    const [collapsed, setCollapsed] = React.useState(field.defaultCollapsed);

    const updateField = (fieldName: string, fieldValue: unknown) => {
        onChange({ ...value, [fieldName]: fieldValue });
    };

    if (field.collapsible) {
        return (
            <div className="border rounded-lg">
                <button
                    type="button"
                    onClick={() => setCollapsed(!collapsed)}
                    className="w-full px-4 py-2 flex items-center justify-between text-left hover:bg-muted/50"
                >
                    <span className="font-medium">{field.label}</span>
                    <span>{collapsed ? '▸' : '▾'}</span>
                </button>
                {!collapsed && (
                    <div className="px-4 py-3 space-y-4 border-t">
                        {field.fields.map((subField) => (
                            <FieldRenderer
                                key={subField.name}
                                field={subField}
                                value={value[subField.name]}
                                onChange={(v) => updateField(subField.name, v)}
                                disabled={disabled}
                                translate={translate}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-4 pl-4 border-l-2 border-muted">
            {field.fields.map((subField) => (
                <FieldRenderer
                    key={subField.name}
                    field={subField}
                    value={value[subField.name]}
                    onChange={(v) => updateField(subField.name, v)}
                    disabled={disabled}
                    translate={translate}
                />
            ))}
        </div>
    );
};

export default FieldRenderer;
